import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Dropdown genérico para o header.
 * Items: [{ label, icon?, onClick?, to?, hint?, badge?, disabled?, divider? }]
 *
 * trigger: conteúdo do botão (ícone, texto, mix).
 * triggerClassName: classes do botão que abre o dropdown.
 * menuClassName: classes extras do popover.
 * align: 'left' | 'right'.
 */
export default function DropdownMenu({
  trigger,
  items,
  triggerClassName = '',
  triggerTitle,
  menuClassName = '',
  align = 'right',
  menuWidth = 'w-56',
  badge,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItem = (item) => {
    if (item.disabled) return;
    setOpen(false);
    if (item.onClick) item.onClick();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`${triggerClassName} relative flex items-center gap-1.5`}
        title={triggerTitle}
      >
        {trigger}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
      {open && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 ${menuWidth} bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1 ${menuClassName}`}>
          {items.map((item, idx) => {
            if (item.divider) {
              return <div key={`div-${idx}`} className="border-t border-slate-100 my-1" />;
            }
            const Icon = item.icon;
            const content = (
              <>
                {Icon && <Icon size={13} className={item.disabled ? 'text-slate-300' : 'text-slate-500'} />}
                <span className="flex-1 truncate">{item.label}</span>
                {item.hint && <span className="text-[10px] text-slate-400">{item.hint}</span>}
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </>
            );
            const commonClass = `w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs ${
              item.disabled ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 hover:bg-slate-50'
            }`;
            if (item.to && !item.disabled) {
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={commonClass}
                >
                  {content}
                </Link>
              );
            }
            return (
              <button
                key={item.label}
                onClick={() => handleItem(item)}
                disabled={item.disabled}
                className={commonClass}
              >
                {content}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
